import React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useField } from 'formik';
import {
  ClickAwayListener,
  FormHelperText,
  Grid,
  InputLabel,
  Tooltip,
  Typography,
  Box,
} from '@mui/material';
import StepReferencePopup from 'components/StepReferencePopup';
import PromptAttachments from 'components/CreateSpark/SparkBuilderDrawers/RightDrawer/forms/PromptAccordion/PromptAttachments';
import { useSchemaContext } from 'sharedComponents/SparkForm/SparkForm';
import { EditorContent } from '@tiptap/react';
import { useTipTapFormField } from './hooks/useTipTapFormField';
import { useReferencePopup } from 'components/common/SparkOutputBoard/helper/useReferencePopup';
import { BOARD_ID_SELECTORS } from 'components/common/SparkOutputBoard/helper/constants';
import type { SparkReferenceValue } from 'types/SparkStepReference';
import type { AttachmentsType } from 'components/CreateSpark/SparkBuilderDrawers/RightDrawer/forms/PromptAccordion';

type TipTapFormFieldProps = {
  name: string;
  isMultiLine?: boolean;
  label?: string;
  helpText?: string;
  subHeaderText?: string;
  placeholderText?: string;
  onchange?: (value: string) => void;
  disableReferenceInsertion?: boolean;
  handleFilesReference?: (value: SparkReferenceValue) => void;
  attachments?: AttachmentsType[];
  onAttachmentRemove?: (
    cardType: AttachmentsType['type'],
    id: AttachmentsType['id']
  ) => void;
};

function TipTapFormField({
  name,
  label,
  helpText,
  subHeaderText,
  placeholderText,
  isMultiLine = false,
  onchange,
  disableReferenceInsertion = false,
  handleFilesReference,
  attachments = [],
  onAttachmentRemove,
}: TipTapFormFieldProps) {
  const promptFieldID = `${BOARD_ID_SELECTORS.PROMPT_FIELD_ID}-${Date.now()}`;

  const { isPopOverOver, anchor, getLastFocusedElement, closePopover } =
    useReferencePopup({ disabled: disableReferenceInsertion });

  const [field, meta, helpers] = useField(name);
  const [initialRender, setInitialRender] = React.useState(true);

  // Use the specialized form field hook
  const { editor, decorateReferences, insertReferenceAtCursor } =
    useTipTapFormField({
      initialContent: field.value || '',
      onChange: async (value) => {
        // Store the plain text value with {{reference}} format
        // This will contain only text with any references in {{id}} format
        helpers.setValue(value);
        onchange && onchange(value);
      },
      multiline: isMultiLine,
      placeholder: placeholderText,
    });

  const ref = React.useRef<HTMLDivElement>(null);

  const { validationSchema } = useSchemaContext();

  // Process any references on initial render and value changes
  React.useEffect(() => {
    if (editor && !disableReferenceInsertion) {
      // Always decorate on initial render
      if (initialRender) {
        decorateReferences();
        setInitialRender(false);
      }
      // Decorate on value changes, but only when not focused
      else if (field.value && !editor.isFocused) {
        decorateReferences();
      }
    }
  }, [
    editor,
    decorateReferences,
    disableReferenceInsertion,
    initialRender,
    field.value,
  ]);

  // Handler for clicking on a reference in the popup
  const handleReferenceClick = React.useCallback(
    (reference: SparkReferenceValue) => {
      if (disableReferenceInsertion) return;

      // Get the reference ID and resource type from the reference object
      let referenceId = reference?.id;
      let resourceType = reference?.resource; // 'inputs' or 'steps'

      if (referenceId) {
        // Format the reference properly with resource type
        const formattedId = resourceType
          ? `${resourceType}.${referenceId}`
          : referenceId;

        if (reference?.resource === 'files') {
          handleFilesReference && handleFilesReference(reference);
          return;
        }

        // Insert the reference at the cursor position with proper format
        insertReferenceAtCursor(formattedId);
      }

      // Close the popup
      // closePopover();
    },
    [
      insertReferenceAtCursor,
      closePopover,
      disableReferenceInsertion,
      handleFilesReference,
    ]
  );

  const isFieldRequired = React.useMemo(() => {
    try {
      const fieldSchema = validationSchema.describe().fields[name];
      // @ts-ignore
      const isRequiredSchema = fieldSchema.tests.find(
        //@ts-ignore
        (test) => test.name === 'requiredTest'
      );
      if (isRequiredSchema) {
        return true;
      }

      if ('optional' in fieldSchema) {
        return !fieldSchema.optional;
      }
      return false;
    } catch {
      return false;
    }
  }, [name, validationSchema]);

  const isError = meta.touched && !!meta.error;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isMultiLine) {
      event.preventDefault();
    }
  };

  const handleClickAway = () => {
    closePopover();
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Grid
        item
        xs={12}
        position='relative'
        component='div'
        ref={ref}
        id={promptFieldID}
      >
        {label && (
          <InputLabel
            sx={{ marginLeft: 1, display: 'flex', gap: 0.5, fontSize: 16 }}
          >
            <span>{isFieldRequired && '*'}</span>
            {label}
            {helpText && (
              <Tooltip title={helpText} placement='top' arrow>
                <InfoOutlinedIcon fontSize='small' />
              </Tooltip>
            )}
          </InputLabel>
        )}
        {subHeaderText && (
          <Typography color='#A3A3A3' marginLeft={1.6} fontSize={12}>
            {subHeaderText}
          </Typography>
        )}
        <Box
          borderRadius='10px'
          border={(theme) => `1px solid ${theme.palette.borderColor}`}
          onClick={(e) => getLastFocusedElement(e, promptFieldID)}
          height='auto'
          maxHeight='18.75rem'
          display='flex'
          flexDirection={isMultiLine ? 'column' : 'row'}
          alignItems={'center'}
          minHeight={isMultiLine ? '8rem' : '56.88px'}
          sx={{
            overflowY: `${isMultiLine ? 'scroll' : 'hidden'}`,
            overflowX: 'hidden',
            '& div': {
              width: '100%',
            },
            '.tiptap': {
              minHeight:
                isMultiLine && attachments.length === 0
                  ? '8rem'
                  : attachments.length > 0
                    ? '5rem'
                    : 'auto',
              padding: '0.9rem',
              outline: 'none',
              width: 'calc(100% - 0.9rem)',
            },
            '.ProseMirror': {
              height: '100%',
              outline: 'none',
            },
            '.ProseMirror p': {
              margin: 0,
            },
            '.reference': {
              backgroundColor: '#E6F4FA',
              borderRadius: '4px',
              padding: '0 4px',
              color: '#0078D4',
            },
          }}
        >
          {attachments && attachments.length > 0 && (
            <Box>
              <PromptAttachments
                attachments={attachments}
                onAttachmentRemove={onAttachmentRemove}
              />
            </Box>
          )}
          <EditorContent
            editor={editor}
            onKeyDown={handleKeyDown}
            style={{ height: '100%' }}
          />
        </Box>

        <FormHelperText
          sx={{
            color: (theme) => theme.palette.error.main,
            marginLeft: 1,
            marginBottom: 0.5,
          }}
        >
          {isError ? meta.error : ' '}
        </FormHelperText>

        {isPopOverOver && (
          <Box
            sx={{
              '& .MuiPaper-root': {
                minWidth: '30rem',
                borderRadius: '10px',
                userSelect: 'none',
              },
            }}
          >
            <StepReferencePopup
              onClose={handleClickAway}
              handleTagClick={handleReferenceClick}
              anchorEl={anchor}
              placement='left-start'
            />
          </Box>
        )}
      </Grid>
    </ClickAwayListener>
  );
}
export default TipTapFormField;
